// src/components/GenerateBackgroundReplacerModal.jsx
import React, { useState, useMemo, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { useAuth } from "@clerk/clerk-react";
import { X, Type, Plus, Trash2, Loader2 } from "lucide-react";
import Loader from "./Loader";

/* ---- Steps: only Assets + Background ---- */
const steps = [
  { number: 1, label: "Assets" },
  { number: 2, label: "Background" },
];

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL ||
  "https://ecom-ads-ai-backend-production.up.railway.app";

/* ---- Keep the same stepper look/feel as your reference modal ---- */
function StepperHeader({ step }) {
  return (
    <div className="flex items-center gap-2 px-8 pt-7 pb-2">
      {steps.map(({ number, label }) => (
        <React.Fragment key={number}>
          <span
            className={`rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold
              ${step === number ? "bg-pink-600 text-white" : "bg-[#29294a] text-gray-400"}`}
          >
            {number}
          </span>
          <span className="text-base sm:text-lg text-white font-semibold mr-8">
            {label}
          </span>
        </React.Fragment>
      ))}
    </div>
  );
}
StepperHeader.propTypes = { step: PropTypes.number.isRequired };

/* ---- Strong default prompt (mirrors backend DEFAULT_ECOM_PROMPT) ---- */
const DEFAULT_ECOM_PROMPT = [
  "studio-quality e-commerce background that highlights the product;",
  "clean minimal scene with subtle gradient or premium surface;",
  "softbox lighting at 45°, gentle natural shadows under the product;",
  "high edge contrast, color harmony that complements the product;",
  "no text, no logos, no people, no hands, no props touching the product;",
  "tasteful shallow depth-of-field; ad-ready, professional look;",
  "composition centered, ideal for marketplace and social ads",
].join(" ");

export default function GenerateBackgroundReplacerModal({
  productImages = [],
  onClose,
  onGenerate,
}) {
  const { getToken } = useAuth();

  const [step, setStep] = useState(1);
  const [selectedImage, setSelectedImage] = useState(null); // url | null
  const [prompt, setPrompt] = useState(DEFAULT_ECOM_PROMPT);

  // ✅ Step 1 manual upload (EXACT flow behavior from Ad Marketing Video step 1)
  const [step1UploadedAsset, setStep1UploadedAsset] = useState(null); // { id, url } | null
  const [step1UploadLoading, setStep1UploadLoading] = useState(false);
  const step1FileInputRef = useRef(null);

  const [error, setError] = useState("");

  // treat undefined/null as “still loading”
  const isAssetsInitialLoading = productImages == null;

  // normalize assets: accept string[] or {id,url}[]
  const normalizedAssets = useMemo(
    () =>
      (productImages || [])
        .map((img, i) =>
          typeof img === "string"
            ? { id: `img-${i}`, url: img }
            : { id: img.id || `img-${i}`, url: img.url }
        )
        .filter((a) => !!a.url),
    [productImages]
  );

  const canNext =
    step === 1 ? !!selectedImage : !!selectedImage && !!prompt.trim();
  const canGenerate = !!selectedImage && !!prompt.trim();

  const handleNext = () => {
    if (!canNext) return;
    setStep(2);
  };
  const handlePrev = () => setStep((s) => Math.max(1, s - 1));

  const handleGenerate = () => {
    if (!canGenerate) return;
    onGenerate(selectedImage, { prompt: prompt.trim() });
    onClose();
  };

  const handleStep1Upload = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      if (!file) return;

      setStep1UploadLoading(true);
      setError("");

      try {
        if (file.size > 20 * 1024 * 1024) {
          setError("Image is too large. Please upload a file under 20MB.");
          return;
        }

        const token = await getToken();
        const formData = new FormData();
        formData.append("file", file);

        // ✅ Reuse existing upload endpoint (same as Ad Marketing Video)
        const { data } = await axios.post(
          `${BACKEND_URL}/api/videos/marketing/endcard/upload`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );

        const url = data?.url;
        if (url) {
          const newAsset = { id: `asset-upload-${Date.now()}`, url };
          setStep1UploadedAsset(newAsset);
          setSelectedImage(url);
        } else {
          setError("Upload did not return a valid URL.");
        }
      } catch {
        setError("Failed to upload image.");
      } finally {
        setStep1UploadLoading(false);
        event.target.value = "";
      }
    },
    [getToken]
  );

  const isUploadedSelected = useMemo(() => {
    if (!step1UploadedAsset) return false;
    return selectedImage === step1UploadedAsset.url;
  }, [selectedImage, step1UploadedAsset]);

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 px-2 py-4">
      <div className="relative bg-[#191930] w-[880px] max-w-[98vw] h-[82vh] rounded-2xl shadow-2xl flex flex-col min-h-[520px] overflow-hidden">
        {/* Close */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close modal"
          className="absolute top-4 right-4 bg-white text-black p-2 rounded-full hover:bg-gray-100 z-10"
        >
          <X size={22} />
        </button>

        {/* Stepper */}
        <StepperHeader step={step} />

        {/* Content (scrolls) */}
        <div className="flex-1 px-8 py-6 overflow-y-auto" style={{ minHeight: 480 }}>
          {/* Step 1: Assets */}
          {step === 1 && (
            <>
              <p className="mb-4 text-gray-300 flex items-center gap-4">
                Select a product image to change its background.
                <span className="text-gray-400 text-sm">
                  Selected {selectedImage ? 1 : 0} / 1
                </span>
              </p>

              {error ? (
                <div className="mb-3 text-red-500 font-semibold text-sm">{error}</div>
              ) : null}

              <input
                ref={step1FileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleStep1Upload}
              />

              {isAssetsInitialLoading ? (
                <div
                  className="flex items-center justify-center py-10 select-none"
                  style={{ minHeight: 480 }}
                >
                  <Loader />
                </div>
              ) : (
                <>
                  <div className="rounded-lg border border-[#23233a] bg-[#191930] px-4 py-3">
                    <div className="grid grid-cols-5 gap-4">
                      {/* ✅ Upload tile (same behavior as Ad Marketing Video step 1) */}
                      <div
                        className={`relative rounded-lg overflow-hidden border-2 group transition
                          ${
                            step1UploadedAsset
                              ? isUploadedSelected
                                ? "border-pink-500 ring-2 ring-pink-600 cursor-pointer"
                                : "border-transparent hover:border-pink-400 cursor-pointer"
                              : "border-[#3A3A4A] border-dashed hover:border-pink-400 cursor-pointer"
                          } ${step1UploadLoading ? "cursor-wait" : ""}`}
                        onClick={() => {
                          if (step1UploadLoading) return;

                          if (step1UploadedAsset) {
                            setSelectedImage(step1UploadedAsset.url);
                            return;
                          }

                          step1FileInputRef.current?.click();
                        }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            if (step1UploadLoading) return;

                            if (step1UploadedAsset) {
                              setSelectedImage(step1UploadedAsset.url);
                              return;
                            }

                            step1FileInputRef.current?.click();
                          }
                        }}
                        aria-label={step1UploadedAsset ? "Uploaded asset" : "Upload image"}
                      >
                        <div className="aspect-[4/5] bg-black relative h-[150px] md:h-[170px]">
                          {step1UploadedAsset ? (
                            <img
                              className="object-cover w-full h-full"
                              src={step1UploadedAsset.url}
                              alt="Uploaded asset"
                              loading="lazy"
                              draggable={false}
                            />
                          ) : (
                            <div className="w-full h-full flex flex-col items-center justify-center bg-[#151529]">
                              <div className="w-12 h-12 rounded-full bg-[#23233a] border border-[#2B2B4A] flex items-center justify-center mb-3">
                                <Plus size={22} className="text-white" />
                              </div>
                              <div className="text-white font-semibold text-sm">Upload image</div>
                              <div className="text-gray-400 text-[11px] mt-1 px-3 text-center">
                                JPG, PNG, WEBP
                              </div>
                            </div>
                          )}

                          {step1UploadLoading && (
                            <div className="absolute inset-0 bg-black/55 flex items-center justify-center">
                              <Loader2 className="animate-spin text-white" />
                            </div>
                          )}

                          <div className="absolute inset-0 bg-black/10 group-hover:bg-pink-600/10 transition" />
                        </div>

                        {/* Actions (only when uploaded) */}
                        {step1UploadedAsset && (
                          <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition">
                            <button
                              type="button"
                              className="px-2.5 py-1.5 rounded-md bg-black/70 hover:bg-black/85 text-white text-xs font-semibold border border-white/10 disabled:opacity-60 disabled:cursor-not-allowed"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (step1UploadLoading) return;
                                step1FileInputRef.current?.click();
                              }}
                              aria-label="Replace uploaded image"
                              disabled={step1UploadLoading}
                            >
                              Replace
                            </button>
                            <button
                              type="button"
                              className="p-2 rounded-md bg-black/70 hover:bg-black/85 text-white border border-white/10 disabled:opacity-60 disabled:cursor-not-allowed"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (step1UploadLoading) return;

                                const uploadedUrl = step1UploadedAsset.url;
                                setStep1UploadedAsset(null);
                                if (selectedImage === uploadedUrl) {
                                  setSelectedImage(null);
                                }
                              }}
                              aria-label="Remove uploaded image"
                              disabled={step1UploadLoading}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Existing analyzed/product assets */}
                      {normalizedAssets.map((asset) => {
                        const isSelected = selectedImage === asset.url;
                        return (
                          <button
                            type="button"
                            key={asset.id}
                            className={`relative rounded-lg overflow-hidden border-2 cursor-pointer group transition
                              ${
                                isSelected
                                  ? "border-pink-500 ring-2 ring-pink-600"
                                  : "border-transparent hover:border-pink-400"
                              }`}
                            onClick={() => setSelectedImage(asset.url)}
                            aria-label="Select asset"
                            title="Select"
                          >
                            <div className="aspect-[4/5] bg-black h-[150px] md:h-[170px]">
                              <img
                                className="object-cover w-full h-full"
                                src={asset.url}
                                alt="Asset"
                                loading="lazy"
                                draggable={false}
                              />
                            </div>
                            <div className="absolute inset-0 bg-black/10 group-hover:bg-pink-600/10 transition" />
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {normalizedAssets.length === 0 ? (
                    <div className="text-white/70 text-center py-6" style={{ minHeight: 140 }}>
                      No images found for this product. You can upload one above.
                    </div>
                  ) : null}
                </>
              )}
            </>
          )}

          {/* Step 2: Background (prompt textarea only) */}
          {step === 2 && (
            <div className="bg-[#1c1c33] rounded-2xl p-5 border border-[#2A2A40]">
              <p className="text-lg font-semibold mb-3 flex items-center gap-2 text-white">
                <Type size={18} /> Describe the background (editable)
              </p>
              <textarea
                placeholder="Describe an ad-perfect, product-focused background…"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={8}
                className="w-full bg-[#121226] border border-[#2A2A40] rounded-xl px-3 py-3 outline-none text-white"
              />
              <p className="text-xs text-gray-400 mt-2">
                We’ll always use this prompt to create a studio-grade, ad-ready background.
                Output is PNG.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between px-8 py-4 border-t border-[#2B2B4A]">
          <button
            type="button"
            onClick={handlePrev}
            disabled={step === 1}
            className={`rounded-lg font-semibold px-6 py-2 ${
              step === 1
                ? "bg-gray-600 cursor-not-allowed"
                : "bg-[#2B2B4A] hover:bg-[#3D3D6E] text-white"
            }`}
          >
            Previous
          </button>

          {step < 2 ? (
            <button
              type="button"
              onClick={handleNext}
              disabled={!canNext || step1UploadLoading}
              className="rounded-lg bg-pink-600 font-semibold px-6 py-2 hover:bg-pink-700 text-white disabled:opacity-60 disabled:cursor-not-allowed"
            >
              Next
            </button>
          ) : (
            <button
              type="button"
              onClick={handleGenerate}
              disabled={!canGenerate}
              className="rounded-lg bg-pink-600 font-semibold px-6 py-2 hover:bg-pink-700 text-white disabled:opacity-60 disabled:cursor-not-allowed"
            >
              Generate
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

GenerateBackgroundReplacerModal.propTypes = {
  productImages: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        url: PropTypes.string,
      })
    ),
  ]),
  onClose: PropTypes.func.isRequired,
  onGenerate: PropTypes.func.isRequired,
};
